/**
 * @description
 * Custom React hook for managing VSCode Lens data streaming.
 * Handles SSE connections, event parsing, state management, and error recovery.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date October 8, 2025
 * @see admin/src/frontend/hooks/useVSCodeLens.test.ts
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { api } from '../services/api';
import { useAuth } from './useAuth';
import type {
  TableInfo,
  FilterConfig,
  StreamRecord,
  StreamStatus,
  StreamEvent,
  ExportFormat,
  DomainAggregation,
  DomainStatistics,
  DomainFilterConfig,
  UserAggregation,
  UserStatistics,
  UserFilterConfig,
  AnalyticsViewMode,
} from '../../types/vscode-lens';
import {
  analyzeDomains,
  filterByDomain,
  calculateDomainStatistics,
  analyzeUsers,
  filterByUser,
  calculateUserStatistics
} from '../utils/domainUtils';

interface UseVSCodeLensReturn {
  // Table management
  tables: TableInfo[];
  tablesLoading: boolean;
  tablesError: string | null;
  
  // Streaming
  isStreaming: boolean;
  streamData: StreamRecord[];
  rawStreamData: StreamRecord[];
  streamStatus: StreamStatus;
  streamError: string | null;
  
  // Domain filtering
  domainAggregation: DomainAggregation | null;
  domainStatistics: DomainStatistics | null;
  domainFilterConfig: DomainFilterConfig;
  setDomainFilterConfig: (config: DomainFilterConfig) => void;
  
  // User analytics
  userAggregation: UserAggregation | null;
  userStatistics: UserStatistics | null;
  userFilterConfig: UserFilterConfig;
  setUserFilterConfig: (config: UserFilterConfig) => void;
  
  // Analytics view mode
  analyticsViewMode: AnalyticsViewMode;
  setAnalyticsViewMode: (mode: AnalyticsViewMode) => void;
  
  // Column filtering
  currentColumnFilters: Record<string, string>;
  updateColumnFilters: (filters: Record<string, string>) => void;
  
  // Actions
  startStream: (table: string, filters: FilterConfig) => Promise<void>;
  stopStream: () => void;
  clearData: () => void;
  exportData: (format: ExportFormat) => void;
  refreshTables: () => Promise<void>;
}

/**
 * Custom hook for VSCode Lens data streaming
 */
export function useVSCodeLens(): UseVSCodeLensReturn {
  const { user } = useAuth();
  
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [tablesLoading, setTablesLoading] = useState(true);
  const [tablesError, setTablesError] = useState<string | null>(null);
  
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamData, setStreamData] = useState<StreamRecord[]>([]);
  const [rawStreamData, setRawStreamData] = useState<StreamRecord[]>([]);
  const [streamStatus, setStreamStatus] = useState<StreamStatus>({
    status: 'idle',
    recordsStreamed: 0,
    totalRecords: 0,
    percentComplete: 0,
  });
  const [streamError, setStreamError] = useState<string | null>(null);
  
  // Track both total server records and filtered records for client users
  const [totalServerRecords, setTotalServerRecords] = useState(0);
  const [filteredRecordCount, setFilteredRecordCount] = useState(0);
  
  // Current column filters for client-side filtering
  const [currentColumnFilters, setCurrentColumnFilters] = useState<Record<string, string>>({});
  
  // Domain filtering state
  const [domainFilterConfig, setDomainFilterConfig] = useState<DomainFilterConfig>({
    enabled: false,
    selectedDomains: new Set(),
    excludedDomains: new Set(),
    mode: 'all',
    column: 'user_email',
  });
  
  // User filtering state
  const [userFilterConfig, setUserFilterConfig] = useState<UserFilterConfig>({
    enabled: false,
    selectedUsers: new Set(),
    excludedUsers: new Set(),
    mode: 'all',
    column: 'user_email',
  });
  
  // Analytics view mode
  const [analyticsViewMode, setAnalyticsViewMode] = useState<AnalyticsViewMode>('domain');
  
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const streamIdRef = useRef<string | null>(null);

  // Compute domain aggregation from raw stream data
  const domainAggregation = useMemo(() => {
    if (streamData.length === 0) return null;
    
    // Only use user_email for domain analytics, not author_email
    const emailColumns = ['user_email'];
    
    // Verify that user_email column exists in the data
    if (streamData.length > 0 && !Object.prototype.hasOwnProperty.call(streamData[0], 'user_email')) {
      return null;
    }
    
    // Set default column if not set
    if (!domainFilterConfig.column) {
      setDomainFilterConfig(prev => ({
        ...prev,
        column: 'user_email',
      }));
    }
    
    return analyzeDomains(streamData, emailColumns);
  }, [streamData, domainFilterConfig.column]);

  // Compute domain statistics
  const domainStatistics = useMemo(() => {
    if (!domainAggregation) return null;
    return calculateDomainStatistics(domainAggregation);
  }, [domainAggregation]);

  // Compute user aggregation from raw stream data
  const userAggregation = useMemo(() => {
    if (streamData.length === 0) return null;
    
    // Only use user_email for user analytics
    const emailColumns = ['user_email'];
    
    // Verify that user_email column exists in the data
    if (streamData.length > 0 && !Object.prototype.hasOwnProperty.call(streamData[0], 'user_email')) {
      return null;
    }
    
    // Set default column if not set
    if (!userFilterConfig.column) {
      setUserFilterConfig(prev => ({
        ...prev,
        column: 'user_email',
      }));
    }
    
    return analyzeUsers(streamData, emailColumns);
  }, [streamData, userFilterConfig.column]);

  // Compute user statistics
  const userStatistics = useMemo(() => {
    if (!userAggregation) return null;
    return calculateUserStatistics(userAggregation);
  }, [userAggregation]);

  // Filter stream data by domain and user configuration
  const filteredStreamData = useMemo(() => {
    let filtered = streamData;
    
    // Apply domain filtering first
    if (domainFilterConfig.enabled) {
      filtered = filterByDomain(filtered, domainFilterConfig);
    }
    
    // Apply user filtering second
    if (userFilterConfig.enabled) {
      filtered = filterByUser(filtered, userFilterConfig);
    }
    
    return filtered;
  }, [streamData, domainFilterConfig, userFilterConfig]);

  // Load tables on mount
  useEffect(() => {
    loadTables();
  }, []);

  const loadTables = async () => {
    try {
      setTablesLoading(true);
      setTablesError(null);
      const data = await api.getVSCodeLensTables();
      setTables(data);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load tables';
      
      // Check if it's a configuration error
      if (message.includes('not configured') || message.includes('API key')) {
        setTablesError('VSCodeLens is not configured. Please configure API key and URL in Settings.');
        setTables([]); // Clear tables for configuration error
      } else {
        setTablesError(message);
      }
      
      console.error('Error loading tables:', error);
    } finally {
      setTablesLoading(false);
    }
  };

  const refreshTables = useCallback(async () => {
    await loadTables();
  }, []);

  /**
   * Extract domain from email address
   */
  const extractDomain = (email: string): string => {
    const match = email.match(/@([^@]+)$/);
    return match ? match[1].toLowerCase() : '';
  };

  /**
   * Check if a data record matches the user's domain
   */
  const matchesUserDomain = (record: any, userDomain: string): boolean => {
    const emailFields = [
      'user_email', 'author_email', 'committer_email',
      'email', 'userEmail', 'authorEmail', 'committerEmail'
    ];
    
    for (const field of emailFields) {
      if (record[field] && typeof record[field] === 'string') {
        const recordDomain = extractDomain(record[field]);
        if (recordDomain === userDomain) {
          return true;
        }
      }
    }
    
    return false;
  };

  /**
   * Filter record by user domain - admin users see all data, client users see only their domain
   */
  const filterRecordByUserDomain = (record: any): boolean => {
    // Admin users see all data
    if (!user || user.role === 'admin' || user.isSuperAdmin) {
      return true;
    }
    
    // Client users only see their domain
    if (user.clientDomain) {
      return matchesUserDomain(record, user.clientDomain);
    }
    
    // Default: don't show
    return false;
  };

  /**
   * Apply column filters to records client-side
   */
  const applyColumnFilters = (records: StreamRecord[], columnFilters: Record<string, string>): StreamRecord[] => {
    if (!columnFilters || Object.keys(columnFilters).length === 0) {
      return records;
    }

    return records.filter(record => {
      return Object.entries(columnFilters).every(([column, filterValue]) => {
        const recordValue = record[column];
        if (recordValue === null || recordValue === undefined) {
          return false;
        }
        
        // Convert to string for comparison
        const recordStr = String(recordValue).toLowerCase();
        const filterStr = filterValue.toLowerCase();
        
        // Support partial matching
        return recordStr.includes(filterStr);
      });
    });
  };

  /**
   * Parse SSE event from text chunk
   */
  const parseSSEEvent = (text: string): StreamEvent | null => {
    const lines = text.split('\n');
    let eventType: string | null = null;
    let eventData: string | null = null;

    for (const line of lines) {
      const trimmedLine = line.trim();
      
      if (trimmedLine.startsWith('event:')) {
        eventType = trimmedLine.substring(6).trim();
      } else if (trimmedLine.startsWith('data:')) {
        // Handle multi-line data by accumulating all data lines
        const dataLine = trimmedLine.substring(5).trim();
        
        // Skip empty data lines (keep-alive events)
        if (dataLine === '') {
          continue;
        }
        
        if (eventData === null) {
          eventData = dataLine;
        } else {
          eventData += dataLine; // Don't add newline for JSON data
        }
      }
    }

    // Default event type if not specified
    if (!eventType) {
      eventType = 'data';
    }

    if (eventData && eventData.trim()) {
      try {
        // Validate JSON before parsing
        const trimmedData = eventData.trim();
        if (trimmedData.startsWith('{') && trimmedData.endsWith('}')) {
          return {
            type: eventType as StreamEvent['type'],
            data: JSON.parse(trimmedData),
          };
        } else {
          console.warn('Invalid JSON format:', trimmedData);
          return null;
        }
      } catch (error) {
        console.error('Failed to parse SSE event data:', error);
        console.error('Event type:', eventType);
        console.error('Event data:', eventData);
        console.error('Data length:', eventData.length);
        return null;
      }
    }

    return null;
  };

  /**
   * Process stream events with domain filtering
   */
  const processStreamEvent = (event: StreamEvent) => {
    switch (event.type) {
      case 'start':
        streamIdRef.current = event.data.streamId || null;
        setTotalServerRecords(event.data.totalRows || 0);
        setFilteredRecordCount(0);
        setStreamStatus({
          status: 'streaming',
          recordsStreamed: 0,
          totalRecords: event.data.totalRows || 0,
          percentComplete: 0,
        });
        setIsStreaming(true);
        break;

      case 'data':
        if (event.data.records && event.data.records.length > 0) {
          // Apply domain filtering to each record first
          const domainFilteredRecords = event.data.records.filter(filterRecordByUserDomain);
          
          // Then apply column filters client-side
          const fullyFilteredRecords = applyColumnFilters(domainFilteredRecords, currentColumnFilters);
          
          if (fullyFilteredRecords.length > 0) {
            setStreamData(prev => [...prev, ...fullyFilteredRecords]);
            setRawStreamData(prev => [...prev, ...domainFilteredRecords]); // Keep domain-filtered for analytics
            setFilteredRecordCount(prev => prev + fullyFilteredRecords.length);
          }
        }
        break;

      case 'progress':
        // Update progress with filtered record count for client users
        const currentRecordCount = user?.role === 'admin' || user?.isSuperAdmin 
          ? (event.data.recordsStreamed || 0)
          : filteredRecordCount;
          
        setStreamStatus(prev => ({
          ...prev,
          recordsStreamed: currentRecordCount,
          totalRecords: user?.role === 'admin' || user?.isSuperAdmin 
            ? (event.data.totalRecordsStreamed || prev.totalRecords)
            : totalServerRecords,
          percentComplete: event.data.percentComplete || prev.percentComplete,
        }));
        break;

      case 'complete':
        // For client users, show filtered count; for admin users, show total
        const finalRecordCount = user?.role === 'admin' || user?.isSuperAdmin 
          ? (event.data.totalRecordsStreamed || 0)
          : filteredRecordCount;
          
        setStreamStatus({
          status: 'complete',
          recordsStreamed: finalRecordCount,
          totalRecords: user?.role === 'admin' || user?.isSuperAdmin 
            ? (event.data.totalRecordsStreamed || 0)
            : totalServerRecords, // Show total server records for context
          percentComplete: 100,
        });
        setIsStreaming(false);
        break;

      case 'error':
        setStreamError(event.data.error || 'Stream error occurred');
        setStreamStatus(prev => ({ ...prev, status: 'error' }));
        setIsStreaming(false);
        break;
    }
  };

  const startStream = useCallback(async (table: string, filters: FilterConfig) => {
    try {
      // Clean up existing stream
      if (readerRef.current) {
        await readerRef.current.cancel();
        readerRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Reset state
      setStreamData([]);
      setStreamError(null);
      setStreamStatus({
        status: 'connecting',
        recordsStreamed: 0,
        totalRecords: 0,
        percentComplete: 0,
      });
      
      // Create new abort controller
      abortControllerRef.current = new AbortController();
      
      // Start stream
      const response = await api.createVSCodeLensStream(table, filters);
      
      if (!response.body) {
        throw new Error('Response body is null');
      }

      const reader = response.body.getReader();
      readerRef.current = reader;
      
      const decoder = new TextDecoder();
      let buffer = '';

      // Read stream
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          // Process any remaining data in buffer
          if (buffer.trim()) {
            const event = parseSSEEvent(buffer);
            if (event) {
              processStreamEvent(event);
            }
          }
          break;
        }

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });
        
        // Process complete events from buffer
        // Split on double newlines to separate events
        const eventSeparator = '\n\n';
        const events = buffer.split(eventSeparator);
        
        // Keep the last (potentially incomplete) event in buffer
        buffer = events.pop() || '';

        for (const eventText of events) {
          if (eventText.trim()) {
            const event = parseSSEEvent(eventText);
            if (event) {
              processStreamEvent(event);
            }
          }
        }
      }
      
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        // Stream was intentionally stopped
        return;
      }
      const message = error instanceof Error ? error.message : 'Failed to start stream';
      setStreamError(message);
      setIsStreaming(false);
      setStreamStatus(prev => ({ ...prev, status: 'error' }));
      console.error('Stream error:', error);
    }
  }, []);

  const stopStream = useCallback(async () => {
    if (readerRef.current) {
      try {
        await readerRef.current.cancel();
      } catch (error) {
        console.error('Error canceling reader:', error);
      }
      readerRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
    setStreamStatus(prev => ({ ...prev, status: 'idle' }));
  }, []);

  const clearData = useCallback(() => {
    setStreamData([]);
    setRawStreamData([]);
    setTotalServerRecords(0);
    setFilteredRecordCount(0);
    setCurrentColumnFilters({});
    setStreamStatus({
      status: 'idle',
      recordsStreamed: 0,
      totalRecords: 0,
      percentComplete: 0,
    });
    setStreamError(null);
  }, []);

  /**
   * Update column filters and re-filter existing data
   */
  const updateColumnFilters = useCallback((newColumnFilters: Record<string, string>) => {
    setCurrentColumnFilters(newColumnFilters);
    
    // Re-filter existing rawStreamData with new column filters
    if (rawStreamData.length > 0) {
      const filteredData = applyColumnFilters(rawStreamData, newColumnFilters);
      setStreamData(filteredData);
      setFilteredRecordCount(filteredData.length);
    }
  }, [rawStreamData]);

  const exportData = useCallback((format: ExportFormat) => {
    if (streamData.length === 0) {
      console.warn('No data to export');
      return;
    }
    
    let content: string;
    let mimeType: string;
    let filename: string;
    
    if (format === 'csv') {
      // Convert to CSV
      const headers = Object.keys(streamData[0]);
      const rows = streamData.map(record => 
        headers.map(header => {
          const value = record[header];
          // Escape quotes and wrap in quotes if contains comma or newline
          const stringValue = String(value ?? '');
          if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        }).join(',')
      );
      content = [headers.join(','), ...rows].join('\n');
      mimeType = 'text/csv;charset=utf-8;';
      filename = `vscode-lens-export-${Date.now()}.csv`;
    } else {
      // Convert to JSON
      content = JSON.stringify(streamData, null, 2);
      mimeType = 'application/json;charset=utf-8;';
      filename = `vscode-lens-export-${Date.now()}.json`;
    }
    
    // Download file
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [streamData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (readerRef.current) {
        readerRef.current.cancel().catch(console.error);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    tables,
    tablesLoading,
    tablesError,
    isStreaming,
    streamData,
    rawStreamData,
    streamStatus,
    streamError,
    domainAggregation,
    domainStatistics,
    domainFilterConfig,
    setDomainFilterConfig,
    userAggregation,
    userStatistics,
    userFilterConfig,
    setUserFilterConfig,
    analyticsViewMode,
    setAnalyticsViewMode,
    currentColumnFilters,
    updateColumnFilters,
    startStream,
    stopStream,
    clearData,
    exportData,
    refreshTables,
  };
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */